import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TrendingUp, Users, Award, DollarSign, BarChart3, Gift, CheckCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LabPartners = () => {
  const howItWorksSteps = [
    {
      icon: Users,
      title: 'Sign up and get your unique referral link',
      description: 'Quick and easy registration process to get started'
    },
    {
      icon: TrendingUp,
      title: 'Share it with your audience',
      description: 'Via social media, blogs, or communities'
    },
    {
      icon: DollarSign,
      title: 'Earn commissions',
      description: 'Every time someone joins Funded Lab through your link'
    }
  ];

  const benefits = [
    'Competitive commissions (up to 25%)',
    'Lifetime revenue share for top tiers',
    'Exclusive perks and bonuses',
    'Priority payouts and dedicated support',
    'Access to marketing resources'
  ];

  const uniqueBenefits = [
    {
      icon: BarChart3,
      title: 'Gamified Dashboard',
      description: 'Track progress, unlock badges, and earn milestone bonuses'
    },
    {
      icon: Users,
      title: 'Community Builder Rewards',
      description: 'Extra payouts for affiliates who create active trading communities'
    },
    {
      icon: Award,
      title: 'Performance Bonuses',
      description: 'Hit monthly targets and earn extra cash or funded accounts'
    }
  ];

  const faqs = [
    {
      question: 'How do I sign up?',
      answer: 'Click Join Now and complete the quick application.'
    },
    {
      question: 'When do I get paid?',
      answer: 'Monthly payouts (weekly for Pro & on demand for Elite tiers).'
    },
    {
      question: 'What is the minimum commission withdrawal?',
      answer: '$100'
    },
    {
      question: 'Is there a cost to join?',
      answer: 'No, it\'s completely free.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-black via-[#1A1A1A] to-black text-white overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1658065969999-38bf31a5380f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjBnb2xkfGVufDB8fHxibGFja3wxNzYyMDI5OTM3fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'hue-rotate(140deg) saturate(1.5)'
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Your Network. Your Earnings. <span className="text-[#10dfb2]">Your Lab.</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
            Earn up to 25% commission + lifetime revenue share by referring traders to Funded Lab.
          </p>
          <Button size="lg" className="bg-[#10dfb2] text-black hover:bg-[#10dfb2] font-semibold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#10dfb2]/50">
            Join Lab Partners Today
          </Button>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Three simple steps to start earning
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#10dfb2] text-black rounded-full mb-4 font-bold text-2xl">
                      {index + 1}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Step {index + 1}
                    </h3>
                    <p className="text-lg font-semibold text-gray-800 mb-2">{step.title}</p>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Join Lab Partners?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                <CheckCircle className="text-[#10dfb2] flex-shrink-0 mt-1" size={24} />
                <span className="text-lg text-gray-800">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiered Commission Structure */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Tiered Commission Structure
            </h2>
            <p className="text-xl text-gray-600">
              Grow your earnings as you scale
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow-md">
              <thead>
                <tr className="bg-[#1A1A1A] text-white">
                  <th className="py-4 px-6 text-left font-bold text-lg rounded-tl-lg">Tier</th>
                  <th className="py-4 px-6 text-left font-bold text-lg">Commission</th>
                  <th className="py-4 px-6 text-left font-bold text-lg">Requirements</th>
                  <th className="py-4 px-6 text-left font-bold text-lg rounded-tr-lg">Extras</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6 font-bold text-gray-900">Starter</td>
                  <td className="py-4 px-6 text-gray-700">10% per sale</td>
                  <td className="py-4 px-6 text-gray-700">None</td>
                  <td className="py-4 px-6 text-gray-700">Affiliate dashboard + marketing kit</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6 font-bold text-gray-900">Growth</td>
                  <td className="py-4 px-6 text-gray-700">15% + 5% recurring</td>
                  <td className="py-4 px-6 text-gray-700">10 referrals</td>
                  <td className="py-4 px-6 text-gray-700">Everything in Starter + Custom discount codes</td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors bg-[#10dfb2]/10">
                  <td className="py-4 px-6 font-bold text-[#10dfb2]">Pro</td>
                  <td className="py-4 px-6 text-gray-700 font-semibold">20% + lifetime 5%</td>
                  <td className="py-4 px-6 text-gray-700">50 referrals</td>
                  <td className="py-4 px-6 text-gray-700">Everything in prior tiers + Co-branded landing pages</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#10dfb2] rounded-bl-lg">Elite</td>
                  <td className="py-4 px-6 text-gray-700 font-semibold">25% + lifetime 10%</td>
                  <td className="py-4 px-6 text-gray-700">100+ referrals</td>
                  <td className="py-4 px-6 text-gray-700 rounded-br-lg">Everything in prior tiers + Dedicated manager + event invites</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Unique Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Unique Benefits
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {uniqueBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                      <Icon className="text-[#10dfb2]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              FAQ
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg mb-4 shadow-md">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-gray-900 hover:text-[#10dfb2] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-black via-[#1A1A1A] to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to start earning?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of partners earning with Funded Lab
          </p>
          <Button size="lg" className="bg-[#10dfb2] text-black hover:bg-[#10dfb2] font-semibold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#10dfb2]/50">
            Join Lab Partners Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default LabPartners;
